import React from "react";
import PremiumResourcePage, { resourceMoney } from "../../../Components/Dashboard/PremiumResourcePage";
import EstimateUpdate from "../SalesNew/EstimateUpdate";
import { BASE_URL } from "../CommonApi";
import { AES, enc } from "crypto-js";
import swal from "sweetalert";

const decryptUniqueId = () => {
  const encryptedUniqueId = localStorage.getItem("uniqueId1");
  return AES.decrypt(encryptedUniqueId, "1234567812345678").toString(enc.Utf8);
};

const authedPost = async (endpoint, body = {}) => {
  const response = await fetch(BASE_URL + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
    },
    body: JSON.stringify({ uniqueId: decryptUniqueId(), ...body }),
  });
  const responseText = await response.text();
  try {
    return JSON.parse(responseText);
  } catch (error) {
    return {
      status: response.ok ? "success" : "error",
      responseMessage: responseText,
    };
  }
};

const normalizeRows = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  return Object.values(value).find(Array.isArray) || [];
};

const getEstimateDetails = async (estimateNumber) => {
  const data = await authedPost(`/fetchByEstimateNumber/${encodeURIComponent(estimateNumber)}`);
  if (data.status !== "success") {
    throw new Error(data.responseMessage || "Could not fetch estimate details.");
  }
  return normalizeRows(data.responseMessage);
};

const getNextInvoiceNumber = async () => {
  const data = await authedPost("/getCurruntNumberList", { type: "invoice" });
  if (data.status !== "success") {
    throw new Error(data.responseMessage || "Could not fetch invoice number.");
  }
  return data.responseMessage;
};

const toInvoiceItems = (items = []) =>
  items.map((item) => ({
    items: item.items || item.itemDetails || item.itemsDetails || "",
    quantity: item.quantity || "",
    rate: item.rate || "",
    discount: item.discount || "",
    amount: item.amount || "",
    discountType: item.discountType || "",
    size: item.size || "",
    tax: item.tax || "",
  }));

const convertEstimateToInvoice = async (row, tools) => {
  const estimateNumber = row.estimateNumber;
  if (!estimateNumber) return;

  const details = await getEstimateDetails(estimateNumber);
  const estimate = details[0] || row;
  const invoiceNumber = await getNextInvoiceNumber();
  const payload = {
    total: estimate.total,
    invoiceNumber,
    oldEstimateNumber: estimate.estimateNumber || estimateNumber,
    estimateNumber: estimate.estimateNumber || estimateNumber,
    invoiceDate: new Date().toISOString().slice(0, 10),
    dueDate: estimate.expiryDate || estimate.dueDate,
    payButtonNow: estimate.payButtonNow,
    organizationName: estimate.organizationName,
    customerName: estimate.customerName,
    companyName: estimate.companyName || estimate.customerName,
    terms: estimate.terms || estimate.paymentTerms,
    items: toInvoiceItems(details),
    subTotal: estimate.subtotal,
    adjustment: estimate.adjustment,
    totalDue: estimate.total,
    notes: estimate.customerNote || estimate.customerNotes,
    state: estimate.state,
    discountCheck: estimate.discountCheck,
    sizeCheck: estimate.sizeCheck,
    taxCheck: estimate.taxCheck,
    igst: estimate.igst || "",
    cgst: estimate.cgst || "",
    sgst: estimate.sgst || "",
    status: "Draft",
    manualCheck: "No",
    uniqueId: decryptUniqueId(),
  };

  const response = await authedPost("/convertToInvoice", payload);
  if (response.status === "success" || response.status === "Success") {
    swal({ icon: "success", title: "Successfully Converted to Invoice" });
    if (tools && typeof tools.refresh === "function") tools.refresh();
    if (tools && typeof tools.closeDetail === "function") tools.closeDetail();
    return;
  }

  throw new Error(response.responseMessage || "Failed to convert estimate to invoice.");
};

const getCustomerEmail = async (row) => {
  const directEmail = row.email || row.customerEmail || row.customerMail || row.contactEmail;
  if (directEmail || !row.customerId) return directEmail;

  const data = await authedPost(`/fetchCustomerById/${row.customerId}`);
  if (data.status !== "success") return "";

  const customer = normalizeRows(data.responseMessage)[0] || data.responseMessage || {};
  return customer.email || customer.customerEmail || customer.otherEmail || "";
};

const sendEstimateMail = async (row) => {
  const email = await getCustomerEmail(row);
  if (!email) {
    swal({ icon: "warning", title: "No customer email found for this estimate." });
    return;
  }

  const subject = `Estimate ${row.estimateNumber || ""}`.trim();
  const body = `Dear ${row.customerName || "Customer"},\n\nPlease find the estimate details for ${row.estimateNumber || "your estimate"}.\n\nRegards`;
  window.location.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

function Estimates({ navigation }) {
  const handleDocumentAction = async (action, row, tools) => {
    try {
      if (action === "Send Mail") {
        await sendEstimateMail(row);
        return;
      }
      if (action === "Convert to Invoice") {
        await convertEstimateToInvoice(row, tools);
      }
    } catch (error) {
      swal({ icon: "error", title: error.message || "Action failed." });
    }
  };

  return (
    <PremiumResourcePage
      navigation={navigation}
      title="Estimates"
      subtitle="Create, send, and manage customer estimates."
      createRoute="EstimatesNewButton"
      createLabel="+ New Estimate"
      fetchEndpoint="/fetchEstimate"
      fetchBody={{ fromDate: "", toDate: "" }}
      documentType="estimate"
      deleteEndpoint="/deleteByEstimateNumber/:id"
      deleteKey="estimateNumber"
      totalKey="total"
      renderEdit={(row, handleCancel) => (
        <EstimateUpdate
          estimateNumber={row.estimateNumber}
          handleCancel={handleCancel}
        />
      )}
      handledActions={["Send Mail", "Convert to Invoice"]}
      onDocumentAction={handleDocumentAction}
      columns={[
        { key: "estimateDate", label: "Date" },
        { key: "estimateNumber", label: "Estimate #" },
        { key: "referenceNumber", label: "Reference" },
        { key: "customerName", label: "Customer Name" },
        { key: "expiryDate", label: "Expiry Date" },
        { key: "total", label: "Amount", className: "premium-money", render: (row) => resourceMoney(row.total) },
      ]}
    />
  );
}

export default Estimates;
